<template>
  <div class="division-list-container">
    <div class="header-card">
      <div class="header-content">
        <div class="left-section">
          <div class="title-with-badge">
            <h1>学生分班</h1>
            <el-tag type="success" effect="light" class="premium-badge">按步骤完成</el-tag>
          </div>
          <p class="subtitle">先建立一次分班任务，再导入学生名单，设置特殊要求，最后一键生成并导出结果。页面会按流程提示下一步，不需要了解算法。</p>
          <div class="quick-steps">
            <span>1. 建任务</span>
            <span>2. 导名单</span>
            <span>3. 开始分班</span>
            <span>4. 导出结果</span>
          </div>
        </div>
        <div class="header-actions">
          <el-button size="large" class="subject-btn" @click="openSubjectDialog">
            科目设置
          </el-button>
          <el-button type="primary" size="large" class="create-btn" @click="openCreateDialog">
            <el-icon><Plus /></el-icon> 新建一次分班
          </el-button>
        </div>
      </div>
    </div>

    <!-- Workspace Grid -->
    <div v-loading="loading" class="workspace-grid">
      <div v-if="hasLoadedWorkspaces && workspaces.length === 0" class="empty-state">
        <el-empty description="还没有分班任务。先新建一次分班，然后按提示导入学生名单。">
          <el-button type="primary" @click="openCreateDialog">开始新建</el-button>
        </el-empty>
      </div>
      
      <div v-for="ws in workspaces" :key="ws.id" class="workspace-card animate__animated animate__fadeIn">
        <div class="card-glow"></div>
        <div class="card-header">
          <div class="title-row">
            <h3 class="ws-name">{{ ws.name }}</h3>
            <el-tag :type="getStatusType(ws.status)" effect="light">
              {{ getStatusText(ws.status) }}
            </el-tag>
          </div>
          <span class="created-time">创建时间：{{ formatDateTime(ws.created_at) }}</span>
        </div>

        <div class="card-body">
          <div class="stat-row">
            <div class="stat-item">
              <span class="stat-label">准备分成</span>
              <span class="stat-value">{{ ws.config?.class_count || '未设置' }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">均衡科目</span>
              <span class="stat-value">{{ ws.config?.balance_targets?.length || 0 }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">分班方式</span>
              <span class="stat-value-text">{{ getAlgoText(ws.config?.algorithm) }}</span>
            </div>
          </div>

          <div class="progress-section">
            <div class="progress-labels">
              <span>当前进度</span>
              <span>{{ ws.status === 'success' ? 100 : (ws.progress || 0) }}%</span>
            </div>
            <el-progress 
              :percentage="ws.status === 'success' ? 100 : (ws.progress || 0)" 
              :status="ws.status === 'failed' ? 'exception' : (ws.status === 'success' ? 'success' : '')"
              :stroke-width="6" 
              :show-text="false"
            />
            <p class="log-preview" :class="{ 'error-log': ws.status === 'failed' }">
              {{ ws.status === 'failed' ? ws.error_message : (ws.log_message || getNextStepText(ws)) }}
            </p>
          </div>
        </div>

        <div class="card-footer">
          <el-button type="info" plain size="default" @click="openEditDialog(ws)">
            修改班级
          </el-button>
          <el-button type="danger" plain size="default" @click="handleDelete(ws.id)">
            删除
          </el-button>
          <el-button type="primary" size="default" @click="enterWorkspace(ws.id)">
            继续处理 <el-icon><ArrowRight /></el-icon>
          </el-button>
        </div>
      </div>
    </div>

    <!-- Create/Edit Workspace Dialog -->
    <el-dialog
      v-model="dialogVisible"
      :title="isEdit ? '修改分班基本信息' : '新建一次分班'"
      width="600px"
      destroy-on-close
    >
      <el-form :model="form" :rules="rules" ref="formRef" label-position="top">
        <el-form-item label="给这次分班起个名字" prop="name">
          <el-input v-model="form.name" placeholder="例如：2026届高一新生分班" />
        </el-form-item>

        <div class="section-divider">班级基本设置</div>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="要分成几个班" prop="class_count">
              <el-input
                v-model="form.class_count"
                class="class-count-input"
                type="number"
                :min="2"
                :max="50"
                placeholder="直接输入数量，如 12"
                @input="handleClassCountInput"
                @blur="normalizeClassCount"
              >
                <template #append>个班</template>
              </el-input>
              <div class="input-tip">可直接键盘输入，系统会自动生成对应的班级名称。</div>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="分班方式" prop="algorithm">
              <el-select v-model="form.algorithm" style="width: 100%">
                <el-option label="推荐：更均衡，适合正式分班" value="anneal" />
                <el-option label="快速：先出初稿，便于预览" value="snake" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="均衡细致程度" v-if="form.algorithm === 'anneal'">
          <el-select v-model="form.iterations" style="width: 100%">
            <el-option label="快一些：适合先看大概结果" :value="5000" />
            <el-option label="推荐：速度和均衡都比较好" :value="20000" />
            <el-option label="更细致：学生多时会稍慢" :value="50000" />
          </el-select>
        </el-form-item>

        <el-form-item label="班级名称">
          <div class="class-names-preview">
            <el-tag 
              v-for="(name, index) in form.class_names" 
              :key="index"
              closable 
              @close="removeClass(index)"
              class="class-tag"
            >
              {{ name }}
            </el-tag>
            <el-button size="small" class="add-class-btn" @click="addClass">+ 添加</el-button>
          </div>
        </el-form-item>

        <div class="section-divider">均衡偏好设置</div>
        <div style="display: flex; flex-direction: column; gap: 10px; padding: 5px 0 15px 0;">
          <el-checkbox v-model="balanceScoreEnabled">
            <span style="font-size: 14px; font-weight: 550; color: #374151;">均衡学生成绩</span>
            <span style="font-size: 12px; color: #6b7280; margin-left: 5px;">(让各班级平均分尽可能接近)</span>
          </el-checkbox>
          <el-checkbox v-model="balanceGenderEnabled">
            <span style="font-size: 14px; font-weight: 550; color: #374151;">均衡男女比例</span>
            <span style="font-size: 12px; color: #6b7280; margin-left: 5px;">(让各班男女比例尽可能接近)</span>
          </el-checkbox>
          <el-checkbox v-model="balanceTagsEnabled">
            <span style="font-size: 14px; font-weight: 550; color: #374151;">均衡特色标签</span>
            <span style="font-size: 12px; color: #6b7280; margin-left: 5px;">(让特长生、班干部等均匀分布)</span>
          </el-checkbox>
          <el-checkbox v-model="balanceSurnameEnabled">
            <span style="font-size: 14px; font-weight: 550; color: #374151;">均衡姓氏分布</span>
            <span style="font-size: 12px; color: #6b7280; margin-left: 5px;">(避免同姓氏学生扎堆在某些班)</span>
          </el-checkbox>
          <el-checkbox v-model="balanceTopStudentsEnabled">
            <span style="font-size: 14px; font-weight: 550; color: #374151;">尖子生分布保底</span>
            <span style="font-size: 12px; color: #6b7280; margin-left: 5px;">(让各科目Top 10%尖子生均匀分布)</span>
          </el-checkbox>
        </div>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitForm">{{ isEdit ? '保存修改' : '创建并进入下一步' }}</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="subjectDialogVisible" title="分班科目设置" width="620px">
      <div class="subject-toolbar">
        <el-button type="primary" plain @click="importPresetSubjects">导入常用科目</el-button>
        <el-button type="success" @click="openSubjectEdit()">新增科目</el-button>
      </div>
      <el-table :data="divisionSubjects" border size="small" v-loading="subjectLoading">
        <el-table-column prop="sort_order" label="排序" width="90" align="center" />
        <el-table-column prop="name" label="科目名称" min-width="160" />
        <el-table-column label="状态" width="90" align="center">
          <template #default="{ row }">
            <el-tag :type="row.is_enabled ? 'success' : 'info'" size="small">{{ row.is_enabled ? '启用' : '停用' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="220" align="center">
          <template #default="{ row }">
            <el-button link type="primary" @click="openSubjectEdit(row)">编辑</el-button>
            <el-button link :type="row.is_enabled ? 'warning' : 'success'" @click="toggleSubject(row)">
              {{ row.is_enabled ? '停用' : '启用' }}
            </el-button>
            <el-button link type="danger" @click="deleteSubject(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>

    <el-dialog v-model="subjectEditVisible" :title="subjectForm.id ? '编辑分班科目' : '新增分班科目'" width="420px">
      <el-form :model="subjectForm" label-position="top">
        <el-form-item label="科目名称">
          <el-input v-model="subjectForm.name" placeholder="例如：语文" />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="subjectForm.sort_order" :min="0" :max="999" style="width: 100%" />
        </el-form-item>
        <el-form-item label="是否启用">
          <el-switch v-model="subjectForm.is_enabled" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="subjectEditVisible = false">取消</el-button>
        <el-button type="primary" @click="saveSubject">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, computed } from 'vue'
import { useRouter } from 'vue-router'
import { Plus, ArrowRight } from '@element-plus/icons-vue'
import { divisionApi } from '@/api'
import { ElMessage, ElMessageBox } from 'element-plus'

const router = useRouter()
const loading = ref(false)
const submitting = ref(false)
const workspaces = ref([])
const hasLoadedWorkspaces = ref(false)
const subjectDialogVisible = ref(false)
const subjectEditVisible = ref(false)
const subjectLoading = ref(false)
const divisionSubjects = ref([])
const subjectForm = ref({
  id: null,
  name: '',
  sort_order: 0,
  is_enabled: true
})

// Dialog values
const dialogVisible = ref(false)
const isEdit = ref(false)
const editId = ref(null)
const formRef = ref(null)

const form = ref({
  name: '',
  class_count: 4,
  class_names: [],
  algorithm: 'anneal',
  iterations: 20000,
  weights: {
    score: 10,
    gender: 5,
    tags: 2,
    surname: 5,
    top_students: 10
  }
})

const balanceScoreEnabled = computed({
  get: () => (form.value.weights?.score ?? 10) > 0,
  set: (val) => {
    if (!form.value.weights) form.value.weights = { score: 10, gender: 5, tags: 2 }
    form.value.weights.score = val ? 10 : 0
  }
})

const balanceGenderEnabled = computed({
  get: () => (form.value.weights?.gender ?? 5) > 0,
  set: (val) => {
    if (!form.value.weights) form.value.weights = { score: 10, gender: 5, tags: 2 }
    form.value.weights.gender = val ? 5 : 0
  }
})

const balanceTagsEnabled = computed({
  get: () => (form.value.weights?.tags ?? 2) > 0,
  set: (val) => {
    if (!form.value.weights) form.value.weights = { score: 10, gender: 5, tags: 2, surname: 5 }
    form.value.weights.tags = val ? 2 : 0
  }
})

const balanceSurnameEnabled = computed({
  get: () => (form.value.weights?.surname ?? 5) > 0,
  set: (val) => {
    if (!form.value.weights) form.value.weights = { score: 10, gender: 5, tags: 2, surname: 5, top_students: 10 }
    form.value.weights.surname = val ? 5 : 0
  }
})

const balanceTopStudentsEnabled = computed({
  get: () => (form.value.weights?.top_students ?? 10) > 0,
  set: (val) => {
    if (!form.value.weights) form.value.weights = { score: 10, gender: 5, tags: 2, surname: 5, top_students: 10 }
    form.value.weights.top_students = val ? 10 : 0
  }
})

const rules = {
  name: [
    { required: true, message: '请输入分班任务名称', trigger: 'blur' },
    { min: 2, max: 50, message: '名称长度在 2 到 50 个字符之间', trigger: 'blur' }
  ],
  class_count: [
    { required: true, message: '请输入计划分班数量', trigger: 'blur' },
    {
      validator: (rule, value, callback) => {
        const count = Number(value)
        if (!Number.isInteger(count) || count < 2 || count > 50) {
          callback(new Error('班级数量需为 2 到 50 之间的整数'))
          return
        }
        callback()
      },
      trigger: 'blur'
    }
  ],
  algorithm: [
    { required: true, message: '请选择分班算法', trigger: 'change' }
  ]
}

onMounted(async () => {
  await nextTick()
  const load = () => fetchWorkspaces()
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(load, { timeout: 600 })
  } else {
    window.setTimeout(load, 120)
  }
})

const fetchWorkspaces = async () => {
  loading.value = true
  try {
    const data = await divisionApi.getWorkspaces()
    workspaces.value = data
  } catch (error) {
    console.error('Failed to get workspaces:', error)
  } finally {
    loading.value = false
    hasLoadedWorkspaces.value = true
  }
}

const fetchDivisionSubjects = async () => {
  subjectLoading.value = true
  try {
    divisionSubjects.value = await divisionApi.getSubjects()
  } catch (error) {
    console.error('Failed to load division subjects:', error)
  } finally {
    subjectLoading.value = false
  }
}

const openSubjectDialog = async () => {
  subjectDialogVisible.value = true
  await fetchDivisionSubjects()
}

const openSubjectEdit = (row = null) => {
  subjectForm.value = row
    ? {
        id: row.id,
        name: row.name,
        sort_order: row.sort_order || 0,
        is_enabled: !!row.is_enabled
      }
    : {
        id: null,
        name: '',
        sort_order: (divisionSubjects.value.length || 0) + 1,
        is_enabled: true
      }
  subjectEditVisible.value = true
}

const saveSubject = async () => {
  const payload = {
    name: subjectForm.value.name?.trim(),
    sort_order: Number(subjectForm.value.sort_order || 0),
    is_enabled: !!subjectForm.value.is_enabled
  }
  if (!payload.name) {
    ElMessage.warning('请输入科目名称')
    return
  }
  try {
    if (subjectForm.value.id) {
      await divisionApi.updateSubject(subjectForm.value.id, payload)
      ElMessage.success('分班科目已更新')
    } else {
      await divisionApi.createSubject(payload)
      ElMessage.success('分班科目已新增')
    }
    subjectEditVisible.value = false
    await fetchDivisionSubjects()
  } catch (error) {
    console.error(error)
  }
}

const toggleSubject = async (row) => {
  await divisionApi.updateSubject(row.id, {
    name: row.name,
    sort_order: row.sort_order,
    is_enabled: !row.is_enabled
  })
  await fetchDivisionSubjects()
}

const deleteSubject = (row) => {
  ElMessageBox.confirm(
    `确定删除分班科目【${row.name}】吗？已导入学生成绩不会被自动删除。`,
    '删除分班科目',
    { confirmButtonText: '删除', cancelButtonText: '取消', type: 'warning' }
  ).then(async () => {
    await divisionApi.deleteSubject(row.id)
    ElMessage.success('分班科目已删除')
    await fetchDivisionSubjects()
  }).catch(() => {})
}

const importPresetSubjects = async () => {
  await divisionApi.importPresetSubjects()
  ElMessage.success('常用分班科目已导入')
  await fetchDivisionSubjects()
}

const openCreateDialog = () => {
  isEdit.value = false
  editId.value = null
  form.value = {
    name: '',
    class_count: 4,
    class_names: ['1班', '2班', '3班', '4班'],
    algorithm: 'anneal',
    iterations: 20000,
    weights: {
      score: 10,
      gender: 5,
      tags: 2,
      surname: 5,
      top_students: 10
    }
  }
  dialogVisible.value = true
}

const openEditDialog = (ws) => {
  isEdit.value = true
  editId.value = ws.id
  
  const config = ws.config || {}
  form.value = {
    name: ws.name,
    class_count: config.class_count || 4,
    class_names: config.class_names || [],
    algorithm: config.algorithm || 'anneal',
    iterations: config.iterations || 20000,
    weights: {
      score: 10,
      gender: 5,
      tags: 2,
      surname: 5,
      top_students: 10,
      ...(config.weights || {})
    }
  }
  dialogVisible.value = true
}

const generateClassNames = (count) => {
  const classCount = Number(count)
  if (!Number.isInteger(classCount) || classCount < 1) return
  const names = []
  for (let i = 1; i <= classCount; i++) {
    names.push(`${i}班`)
  }
  form.value.class_names = names
}

const handleClassCountInput = (value) => {
  const count = Number(value)
  if (!Number.isInteger(count) || count < 2 || count > 50) return
  generateClassNames(count)
}

const normalizeClassCount = () => {
  const count = Number(form.value.class_count)
  if (!Number.isInteger(count)) return
  const safeCount = Math.min(50, Math.max(2, count))
  form.value.class_count = safeCount
  generateClassNames(safeCount)
}

const removeClass = (index) => {
  form.value.class_names.splice(index, 1)
  form.value.class_count = form.value.class_names.length
}

const addClass = () => {
  const nextNum = form.value.class_names.length + 1
  form.value.class_names.push(`${nextNum}班`)
  form.value.class_count = form.value.class_names.length
}

const submitForm = async () => {
  if (!formRef.value) return
  
  await formRef.value.validate(async (valid) => {
    if (!valid) return
    
    submitting.value = true
    const payload = {
      name: form.value.name,
      config: {
        class_count: Number(form.value.class_count),
        class_names: form.value.class_names,
        algorithm: form.value.algorithm,
        iterations: form.value.iterations,
        weights: form.value.weights,
        balance_targets: form.value.balance_targets || [] // Preserve balance targets if already configured
      }
    }
    
    try {
      if (isEdit.value) {
        // preserve old config targets if exist
        const oldWs = workspaces.value.find(w => w.id === editId.value)
        if (oldWs && oldWs.config && oldWs.config.balance_targets) {
          payload.config.balance_targets = oldWs.config.balance_targets
        }
        await divisionApi.updateWorkspace(editId.value, payload)
        ElMessage.success('配置更新成功')
      } else {
        await divisionApi.createWorkspace(payload)
        ElMessage.success('任务创建成功')
      }
      dialogVisible.value = false
      fetchWorkspaces()
    } catch (error) {
      console.error(error)
    } finally {
      submitting.value = false
    }
  })
}

const handleDelete = (id) => {
  ElMessageBox.confirm(
    '确定要删除此分班任务吗？删除后相关的学生名册、分配结果和备份镜像都将清空，无法恢复！',
    '警告',
    {
      confirmButtonText: '极其确定',
      cancelButtonText: '手滑了',
      type: 'warning'
    }
  ).then(async () => {
    try {
      await divisionApi.deleteWorkspace(id)
      ElMessage.success('分班任务已删除')
      fetchWorkspaces()
    } catch (error) {
      console.error(error)
    }
  })
}

const enterWorkspace = (id) => {
  router.push(`/division/${id}`)
}

const getStatusType = (status) => {
  switch (status) {
    case 'idle': return 'info'
    case 'running': return 'primary'
    case 'success': return 'success'
    case 'failed': return 'danger'
    default: return 'info'
  }
}

const getStatusText = (status) => {
  switch (status) {
    case 'idle': return '未分班'
    case 'running': return '算法计算中'
    case 'success': return '分班已完成'
    case 'failed': return '运算失败'
    default: return status
  }
}

const getAlgoText = (algo) => {
  if (algo === 'snake') return '快速分班'
  if (algo === 'anneal') return '推荐均衡'
  return algo || '未设置'
}

const getNextStepText = (ws) => {
  if (ws.status === 'success') return '分班已完成，可以进入后查看名单或导出 Excel。'
  if (ws.status === 'running') return '正在自动分班，请稍等。'
  return '下一步：进入后导入学生名单，再点击开始分班。'
}

const formatDateTime = (val) => {
  if (!val) return ''
  const d = new Date(val)
  return d.toLocaleString()
}
</script>

<style scoped>
.division-list-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 12px 0 40px 0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.header-card {
  background: #ffffff;
  border: 1px solid #dbe4ef;
  border-radius: 12px;
  padding: 30px 34px;
  color: #172033;
  box-shadow: 0 8px 28px rgba(20, 35, 60, 0.06);
  margin-bottom: 24px;
  position: relative;
  overflow: hidden;
}

.header-card::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  width: 210px;
  height: 100%;
  background: linear-gradient(135deg, rgba(20, 184, 166, 0.12), rgba(37, 99, 235, 0.08));
  z-index: 1;
}

.header-content {
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

.left-section {
  max-width: 780px;
}

.title-with-badge {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.header-card h1 {
  font-size: 30px;
  font-weight: 750;
  margin: 0;
  letter-spacing: 0;
}

.premium-badge {
  font-weight: 600;
  border-radius: 6px;
  border: none;
}

.subtitle {
  font-size: 15px;
  color: #536172;
  line-height: 1.6;
  margin: 0;
}

.quick-steps {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 18px;
}

.quick-steps span {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 0 12px;
  border-radius: 6px;
  background: #f1f7f6;
  border: 1px solid #cfe7e3;
  color: #0f766e;
  font-size: 13px;
  font-weight: 650;
}

.create-btn {
  background: #2563eb !important;
  border: none !important;
  font-weight: 600;
  border-radius: 8px;
  padding: 12px 24px;
  box-shadow: 0 6px 16px rgba(37, 99, 235, 0.24);
  transition: all 0.3s ease;
}

.create-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 18px rgba(37, 99, 235, 0.28);
}

/* Grid Layout */
.workspace-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 18px;
  content-visibility: auto;
  contain-intrinsic-size: 420px;
}

.empty-state {
  grid-column: 1 / -1;
  background: #ffffff;
  padding: 60px;
  border-radius: 10px;
  border: 1px dashed #cbd5e1;
  text-align: center;
}

.workspace-card {
  background: #ffffff;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
  padding: 22px;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.workspace-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
  border-color: #b9c7d8;
}

.card-glow {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(90deg, #14b8a6, #2563eb);
}

.card-header {
  margin-bottom: 20px;
}

.title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 6px;
}

.ws-name {
  font-size: 18px;
  font-weight: 750;
  color: #1e293b;
  margin: 0;
  line-height: 1.3;
}

.created-time {
  font-size: 12px;
  color: #64748b;
}

.stat-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  background: #f8fafc;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 20px;
}

.stat-item {
  text-align: center;
  display: flex;
  flex-direction: column;
}

.stat-item:not(:last-child) {
  border-right: 1px solid #e2e8f0;
}

.stat-label {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 19px;
  font-weight: 750;
  color: #1e293b;
}

.stat-value-text {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  height: 27px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.progress-section {
  margin-bottom: 20px;
}

.progress-labels {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 6px;
}

.log-preview {
  font-size: 12px;
  color: #64748b;
  margin: 8px 0 0 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.error-log {
  color: #ef4444;
  font-weight: 500;
}

.card-footer {
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.card-footer .el-button {
  flex: 1;
  border-radius: 7px;
}

.section-divider {
  font-size: 14px;
  font-weight: 700;
  color: #475569;
  border-left: 4px solid #14b8a6;
  padding-left: 8px;
  margin: 24px 0 16px 0;
}

.dialog-help {
  margin: -8px 0 14px 0;
  color: #64748b;
  font-size: 13px;
}

.class-count-input :deep(.el-input__inner) {
  font-weight: 700;
}

.input-tip {
  margin-top: 6px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.4;
}

.class-names-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  padding: 12px;
  min-height: 50px;
  width: 100%;
}

.class-tag {
  border-radius: 6px;
}

.add-class-btn {
  border-style: dashed !important;
  border-radius: 6px;
}

.header-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.subject-btn {
  border-radius: 10px;
}

.subject-toolbar {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 12px;
}
</style>
